// Décision sur un dossier : résultat (approuvé, approuvé sous conditions, refusé), motif ou
// conditions visibles par l'usager (obligatoire en cas de refus), voies de recours, puis document
// généré à partir du modèle de la version avec prévisualisation et téléchargement.
import { LIBELLES_RESULTAT, type DocumentResume, type DossierDetail, type TransitionDisponible } from '@cc/contrats/ui-metier';
import { Bandeau, Bouton, Panneau, Squelette, ZoneTexte } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { requete } from '../../../api/client';
import { messageErreur } from '../../../api/erreurs';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { urlContenu } from '../listes';
import commun from '../../../commun/commun.module.css';
import styles from '../metier.module.css';

const RESULTATS = ['approuve', 'approuve_sous_conditions', 'refuse'] as const;

function DocumentGenere({ dossier: d }: { dossier: DossierDetail }) {
  const docs = useQuery({
    queryKey: ['documents', 'objet', 'dossier', d.id],
    queryFn: ({ signal }) => requete<{ elements: DocumentResume[] }>('/documents', { signal, parametres: { objet_type: 'dossier', objet_id: d.id } }).then((r) => r.elements),
    retry: false,
    // Le PDF est produit par le travailleur de tâches : on relit la liste tant qu'il n'existe pas.
    refetchInterval: (q) => (d.resultat && !(q.state.data ?? []).some((x) => x.type_document === 'decision') ? 5000 : false),
  });
  const decision = (docs.data ?? []).filter((x) => x.type_document === 'decision').sort((a, b) => (a.cree_le < b.cree_le ? 1 : -1))[0];
  const [url, setUrl] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  useEffect(() => {
    if (!decision || !ouvert) return;
    let actif = true;
    urlContenu(decision.id).then((u) => actif && setUrl(u)).catch((e: unknown) => actif && setErreur(messageErreur(e)));
    return () => {
      actif = false;
    };
  }, [decision, ouvert]);
  if (docs.isPending) return <Squelette lignes={2} />;
  if (docs.error) return <p className={commun.na}>Documents non lisibles : {messageErreur(docs.error)}</p>;
  if (!decision) {
    return <p className={commun.na}>{d.resultat ? 'Document de décision en cours de génération par le travailleur de tâches (ou aucun modèle défini pour cette version).' : 'Le document est généré après la décision, à partir du modèle de la version.'}</p>;
  }
  return (
    <div className={styles.pile}>
      <p>
        <strong>{decision.titre}</strong> : version {decision.version_courante}, {date(decision.cree_le)}
        {decision.code_verification ? <span className={commun.na}>, code de vérification {decision.code_verification}</span> : null}
      </p>
      <div className={commun.ligne}>
        <Bouton variante="secondaire" petit onClick={() => setOuvert((o) => !o)}>
          {ouvert ? 'Masquer la prévisualisation' : 'Prévisualiser le document'}
        </Bouton>
        {url ? (
          <a className={commun.lienSimple} href={url} download={decision.titre}>
            Télécharger
          </a>
        ) : null}
      </div>
      {erreur ? <Bandeau type="erreur">Contenu inaccessible : {erreur}</Bandeau> : null}
      {ouvert && url ? <iframe className={styles.apercu} src={url} title={`Prévisualisation de ${decision.titre}`} /> : ouvert ? <Squelette hauteurBloc={120} libelle="Chargement du document" /> : null}
    </div>
  );
}

export function DecisionDossier({ dossier: d, transition, invalider }: { dossier: DossierDetail; transition: TransitionDisponible | undefined; invalider: (readonly unknown[])[] }) {
  const [resultat, setResultat] = useState<(typeof RESULTATS)[number] | ''>('');
  const [motif, setMotif] = useState('');
  const [recours, setRecours] = useState('');
  const decision = useAction({
    executer: () => requete(`/dossiers/${d.id}/transitions/${transition?.code}`, { methode: 'POST', corps: { resultat, motif: motif.trim() || undefined, voies_recours: recours.trim() || undefined } }),
    succes: 'Décision enregistrée.',
    invalider,
  });
  const refus = resultat === 'refuse';
  return (
    <Panneau titre="Décision">
      {d.resultat ? (
        <dl className={commun.definitions}>
          <dt>Résultat</dt>
          <dd>
            <strong>{LIBELLES_RESULTAT[d.resultat] ?? d.resultat}</strong>
            {d.decide_le ? `, le ${date(d.decide_le)}` : ''}
          </dd>
          {d.motif_decision ? (
            <>
              <dt>Motif ou conditions</dt>
              <dd>{d.motif_decision}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {transition ? (
        <form
          className={styles.pile}
          onSubmit={(e) => {
            e.preventDefault();
            if (resultat && (!refus || motif.trim())) decision.lancer(undefined);
          }}
        >
          <fieldset className={styles.pile} style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="cc-etiquette">Résultat</legend>
            <div className={styles.choixResultat} role="radiogroup" aria-label="Résultat de la décision">
              {RESULTATS.map((r) => (
                <label key={r} className={styles.optionResultat} data-choisi={resultat === r || undefined}>
                  <input type="radio" name="resultat" value={r} checked={resultat === r} onChange={() => setResultat(r)} />
                  <span>{LIBELLES_RESULTAT[r]}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <ZoneTexte libelle="Motif ou conditions, visibles par l'usager" value={motif} rows={3} required={refus} facultatif={!refus} aide={refus ? 'Un refus porte toujours un motif.' : 'Repris dans le document de décision.'} erreur={decision.erreursChamps.find((c) => c.champ === 'motif')?.message} onChange={(e) => setMotif(e.target.value)} />
          <ZoneTexte libelle="Voies de recours" value={recours} rows={2} facultatif onChange={(e) => setRecours(e.target.value)} />
          {decision.messageErreur ? <Bandeau type="erreur">{decision.messageErreur}</Bandeau> : null}
          <div>
            <Bouton type="submit" variante={refus ? 'danger' : 'principal'} chargement={decision.enCours} disabled={!resultat || (refus && motif.trim().length < 3)}>
              {resultat === 'refuse' ? 'Refuser la demande' : 'Enregistrer la décision'}
            </Bouton>
          </div>
          <p className={commun.na}>Après la décision : signature ou notification, puis clôture, depuis les actions du dossier. L'usager est informé sur ses canaux.</p>
        </form>
      ) : !d.resultat ? (
        <p className={commun.na}>La décision sera possible quand le dossier aura atteint l'étape de décision, pour l'autorité compétente ou son délégataire.</p>
      ) : null}
      <h3 className="cc-etiquette">Document généré</h3>
      <DocumentGenere dossier={d} />
    </Panneau>
  );
}
